'use client';

import { memo, useState, useEffect } from 'react';
import { Loader2, Phone, ShieldCheck, User, X } from 'lucide-react';
import { laravelPaths } from '@/lib/site';
import { getNextProxyBase } from '@/lib/nextProxy';
import { ThemedExternalLink } from '@/components/ThemedExternalLink';
import { ListingThumbnail } from '@/components/ListingThumbnail';
import { formatListingDate, listingCreatedAt } from '@/lib/formatListingDate';

export type ClassifiedSellerContact = {
  seller_name?: string | null;
  seller_phone?: string | null;
  seller_email?: string | null;
};

export interface ClassifiedAd {
  id: string | number;
  slug: string;
  title: string;
  price?: number | null;
  is_free?: boolean;
  is_negotiable?: boolean;
  thumbnail_url?: string | null;
  thumbnail_fallback_url?: string | null;
  image?: string | null;
  seller_name?: string | null;
  seller_phone?: string | null;
  seller_email?: string | null;
  ad_type?: string;
  is_boosted?: boolean;
  boost_type?: string | null;
  location?: string | null;
  condition?: string;
  _isAd?: boolean;
  seller_verified?: boolean;
  views?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
}

interface ClassifiedAdCardProps {
  ad: ClassifiedAd;
  hideBadge?: boolean;
}

function formatPrice(ad: ClassifiedAd): string {
  if (ad.is_free) return 'Free';
  if (ad.price == null || ad.price === 0) return 'Price on request';
  return `LKR ${Number(ad.price).toLocaleString()}`;
}

export const ClassifiedAdCard = memo(function ClassifiedAdCard({ ad, hideBadge = false }: ClassifiedAdCardProps) {
  const [showContactModal, setShowContactModal] = useState(false);
  const [contact, setContact] = useState<ClassifiedSellerContact | null>(null);
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const sellerNameCandidates = [
    ad.seller_name,
    (ad as { sellerName?: string | null }).sellerName,
  ];
  const sellerName = sellerNameCandidates.find((name) => typeof name === 'string' && name.trim().length > 0)?.trim() ?? 'Seller';
  const listedLabel = formatListingDate(listingCreatedAt(ad));
  const fallbackImage =
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect width='800' height='600' fill='%23e5e7eb'/%3E%3Cg fill='%239ca3af'%3E%3Crect x='250' y='180' width='300' height='220' rx='18'/%3E%3Ccircle cx='400' cy='290' r='58' fill='%23e5e7eb'/%3E%3Ccircle cx='400' cy='290' r='42'/%3E%3Crect x='285' y='160' width='70' height='28' rx='8'/%3E%3Crect x='470' y='230' width='52' height='24' rx='6'/%3E%3C/g%3E%3C/svg%3E";
  const imageUrl = ad.thumbnail_url ?? ad.image ?? ad.thumbnail_fallback_url ?? fallbackImage;
  const storageFallbackUrl = ad.thumbnail_fallback_url ?? fallbackImage;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showContactModal) setShowContactModal(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showContactModal]);

  useEffect(() => {
    if (!showContactModal) {
      setContact(null);
      setContactStatus('idle');
      return;
    }
    let cancelled = false;
    (async () => {
      setContactStatus('loading');
      setContact(null);
      try {
        const nx = getNextProxyBase();
        if (!ad.slug) {
          setContactStatus('error');
          return;
        }
        const res = await fetch(`${nx}/classified-ads/${encodeURIComponent(ad.slug)}/contact`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (cancelled) return;
        if (!res.ok) {
          setContactStatus('error');
          return;
        }
        const data = (await res.json()) as ClassifiedSellerContact;
        if (!cancelled) {
          setContact(data);
          setContactStatus('ready');
        }
      } catch {
        if (!cancelled) setContactStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showContactModal, ad.slug]);

  const triggerAdImageUpload = () => {
    if (!ad.slug) return;
    fetch(`${getNextProxyBase()}/classified-ads/${encodeURIComponent(ad.slug)}/ensure-images-on-s3`, {
      method: 'GET',
      keepalive: true,
      cache: 'no-store',
    }).catch(() => {});
  };

  return (
    <>
      <ThemedExternalLink
        href={ad.slug ? laravelPaths.classifiedAd(ad.slug) : '#'}
        onClickCapture={triggerAdImageUpload}
        className="no-tap-highlight group relative bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col h-full cursor-pointer block focus:outline-none focus-visible:outline-none focus-visible:ring-0"
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-xl bg-neutral-100 dark:bg-neutral-700">
          {!hideBadge && ad.is_boosted && ad.boost_type && ad.boost_type !== 'none' && (
            <span className="absolute top-2 left-2 md:top-3 md:left-3 z-10 bg-brand-orange text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded shadow-sm uppercase tracking-wide">
              BOOSTED
            </span>
          )}
          <ListingThumbnail
            src={imageUrl}
            alt={ad.title ?? ''}
            fallbackSrc={storageFallbackUrl}
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {(ad.condition === 'New' || ad.condition === 'new') && (
            <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 bg-brand-dark/80 text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded backdrop-blur-sm">
              New
            </div>
          )}
        </div>

        <div className="p-3 md:p-4 flex-1 flex flex-col">
          <div className="flex justify-between items-center gap-1 mb-2 h-[1.25rem] md:h-[1.5rem]">
            <span className="text-[8px] md:text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full truncate max-w-[55%] bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-200">
              CLASSIFIED
            </span>
            {ad.seller_verified && (
              <span className="inline-flex items-center gap-0.5 shrink-0 text-[8px] md:text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200">
                <ShieldCheck size={10} className="md:w-3 md:h-3 shrink-0" aria-hidden />
                Verified
              </span>
            )}
          </div>

          <h3 className="text-brand-dark dark:text-neutral-100 font-medium text-xs md:text-base leading-snug mb-2 h-[1.25rem] md:h-[1.5rem] line-clamp-1 group-hover:text-brand-orange transition-colors">
            {ad.title}
          </h3>

          <div className="mt-0">
            <div className="mb-2 h-[1.75rem] md:h-[2rem] flex items-center">
              <div className="flex items-center gap-1 md:gap-2 min-w-0">
                <span className="text-sm md:text-xl font-bold text-brand-green tracking-tight leading-none whitespace-nowrap">
                  {formatPrice(ad)}
                </span>
              </div>
            </div>
            <div className="mb-2 h-[1rem] md:h-[1.25rem] flex items-center justify-between gap-2 text-[10px] md:text-xs text-neutral-600 dark:text-neutral-400 min-w-0">
              <span className="truncate">{sellerName}</span>
              {listedLabel && (
                <time
                  dateTime={listingCreatedAt(ad) ?? undefined}
                  className="shrink-0 text-neutral-400 dark:text-neutral-500 tabular-nums"
                >
                  {listedLabel}
                </time>
              )}
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowContactModal(true);
              }}
              className="w-full py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 md:gap-2 transition-all duration-200 shadow-sm bg-brand-green text-white hover:bg-brand-green-hover mt-6"
            >
              <Phone size={14} className="md:w-4 md:h-4" />
              Contact Seller
            </button>
          </div>
        </div>
      </ThemedExternalLink>

      {showContactModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowContactModal(false)}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-neutral-200 dark:border-neutral-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-lg md:text-xl font-bold text-neutral-800 dark:text-neutral-100">Contact Seller</h2>
              <button
                type="button"
                onClick={() => setShowContactModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 md:p-6 space-y-4 min-h-[120px]">
              {contactStatus === 'loading' && (
                <div className="flex flex-col items-center justify-center gap-3 py-8 text-neutral-500 dark:text-neutral-400">
                  <Loader2 className="animate-spin text-brand-orange" size={28} />
                  <p className="text-sm">Loading contact details…</p>
                </div>
              )}
              {contactStatus === 'error' && (
                <p className="text-sm text-red-600 dark:text-red-400 py-4">
                  Could not load contact details. Please try again later.
                </p>
              )}
              {contactStatus === 'ready' && contact && (
                <>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-brand-orange/10 rounded-full flex items-center justify-center shrink-0">
                      <User size={20} className="text-brand-orange" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Seller</p>
                      <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                        {contact.seller_name ?? 'Not provided'}
                      </p>
                    </div>
                  </div>
                  {contact.seller_phone && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center shrink-0">
                        <Phone size={20} className="text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Phone</p>
                        <a
                          href={`tel:${contact.seller_phone}`}
                          className="text-base font-semibold text-brand-orange hover:underline"
                        >
                          {contact.seller_phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {!contact.seller_phone && (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">No phone number on file for this listing.</p>
                  )}
                </>
              )}
            </div>
            <div className="p-4 md:p-6 border-t border-neutral-200 dark:border-neutral-700 flex gap-3">
              <button
                type="button"
                onClick={() => setShowContactModal(false)}
                className="flex-1 px-4 py-2.5 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 rounded-lg font-semibold hover:bg-neutral-200 dark:hover:bg-neutral-600"
              >
                Close
              </button>
              {contactStatus === 'ready' && contact?.seller_phone && (
                <a
                  href={`tel:${contact.seller_phone}`}
                  className="flex-1 px-4 py-2.5 bg-brand-orange text-white rounded-lg font-semibold hover:bg-brand-orange-hover text-center"
                >
                  Call Now
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});
